import { Table, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiClipboardDocument, HiXMark } from 'react-icons/hi2';
import {
  useListCustomerUploadLinksQuery,
  useRevokeCustomerUploadLinkMutation,
} from '@/store/customerUploadLinksApi';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useDateFormat } from '@/utils/dates';
import { StatusBadge, ActionButtons, EmptyState } from './CustomerUploadLinksList.styled';

interface CustomerUploadLinksListProps {
  customerId: string;
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}

export default function CustomerUploadLinksList({ customerId }: CustomerUploadLinksListProps) {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  const { data: links, isLoading } = useListCustomerUploadLinksQuery(customerId);
  const [revokeLink] = useRevokeCustomerUploadLinkMutation();

  const handleCopy = async (url: string) => {
    await navigator.clipboard.writeText(url);
  };

  const handleRevoke = async (id: string) => {
    await revokeLink(id);
  };

  const getStatus = (link: { isActive: boolean; isExpired: boolean }) => {
    if (!link.isActive) {
      return { labelKey: 'customerUploadLink.status.revoked', variant: 'secondary' as const };
    }
    if (link.isExpired) {
      return { labelKey: 'customerUploadLink.status.expired', variant: 'danger' as const };
    }
    return { labelKey: 'customerUploadLink.status.active', variant: 'success' as const };
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!links?.length) {
    return <EmptyState>{t('customerUploadLink.list.empty')}</EmptyState>;
  }

  return (
    <Table hover responsive size="sm">
      <thead>
        <tr>
          <th>{t('customerUploadLink.list.created')}</th>
          <th>{t('customerUploadLink.list.expires')}</th>
          <th>{t('customerUploadLink.list.status')}</th>
          <th>{t('customerUploadLink.list.usage')}</th>
          <th>{t('customerUploadLink.list.actions')}</th>
        </tr>
      </thead>
      <tbody>
        {links.map((link) => {
          const status = getStatus(link);
          return (
            <tr key={link.id}>
              <td>{formatDateTime(link.createdAt)}</td>
              <td>
                {link.expiresAt
                  ? formatDateTime(link.expiresAt)
                  : t('customerUploadLink.list.noExpiry')}
              </td>
              <td>
                <StatusBadge $variant={status.variant}>{t(status.labelKey)}</StatusBadge>
              </td>
              <td>
                {formatSize(link.totalUploadedBytes)} / {formatSize(link.maxSizeBytes)}
              </td>
              <td>
                <ActionButtons>
                  {link.isActive && !link.isExpired && (
                    <>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleCopy(link.uploadUrl)}
                        title={t('customerUploadLink.list.copyLink')}
                      >
                        <HiClipboardDocument size="0.875rem" />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRevoke(link.id)}
                        title={t('customerUploadLink.list.revoke')}
                      >
                        <HiXMark size="0.875rem" />
                      </Button>
                    </>
                  )}
                </ActionButtons>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}
